import { useAuthInfo } from "@propelauth/react";
import { useOrg } from "./useOrg";
import { getEnv } from "../config/env";
import type { OrgMemberInfo } from "@propelauth/javascript";

export interface UserRoleInfo {
  isLoggedIn: boolean;
  loading: boolean;
  role: string;
  isAdmin: boolean;
  user: any;
  orgId?: string;
  activeOrg?: OrgMemberInfo | null;
  accessToken?: string;
  isDemo?: boolean;
}

export function useRole(): UserRoleInfo {
  const authInfo = useAuthInfo();
  const { activeOrg, activeOrgId, activeOrgRole } = useOrg();

  const isDemo =
    getEnv("VITE_DEMO_MODE", "false") === "true" ||
    (typeof window !== "undefined" &&
      (window.sessionStorage.getItem("silvestre_demo_mode") === "true" ||
        new URLSearchParams(window.location.search).get("demo") === "true"));

  if (authInfo.loading) {
    return {
      isLoggedIn: false,
      loading: true,
      role: "None",
      isAdmin: false,
      user: null,
      isDemo,
    };
  }

  if (!authInfo.isLoggedIn || !authInfo.user) {
    if (isDemo) {
      return {
        isLoggedIn: true,
        loading: false,
        role: "Admin",
        isAdmin: true,
        user: { email: "operator@silvestre.cr", name: "Operator Admin" },
        orgId: activeOrgId || "org_silvestre_hotel",
        activeOrg: (activeOrg || {
          orgId: "org_silvestre_hotel",
          orgName: "Silvestre Nosara",
          userAssignedRole: "Admin",
        }) as any,
        accessToken: "demo_access_token",
        isDemo: true,
      };
    }
    return {
      isLoggedIn: false,
      loading: false,
      role: "None",
      isAdmin: false,
      user: null,
      isDemo: false,
    };
  }

  const { user, orgHelper, accessToken } = authInfo;
  const adminOrgId = getEnv("VITE_ADMIN_ORG_ID", "").trim();

  let isAdmin = false;
  let adminOrgRole: string | undefined = undefined;

  if (orgHelper && adminOrgId) {
    const adminOrg = orgHelper.getOrg(adminOrgId);
    if (adminOrg) {
      const orgAny = adminOrg as any;
      const orgRole = orgAny.userAssignedRole || orgAny.userRole || orgAny.orgRole;
      const isRoleFn = typeof orgAny.isRole === "function" ? orgAny.isRole.bind(orgAny) : null;

      if (
        orgRole === "Owner" ||
        orgRole === "Admin" ||
        (isRoleFn && (isRoleFn("Owner") || isRoleFn("Admin")))
      ) {
        isAdmin = true;
        adminOrgRole = orgRole || (isRoleFn && isRoleFn("Owner") ? "Owner" : "Admin");
      }
    }
  }

  let displayRole = "Member";
  let primaryOrgId: string | undefined = activeOrgId || undefined;

  if (isAdmin) {
    displayRole = adminOrgRole || "Admin";
    primaryOrgId = adminOrgId || primaryOrgId;
  } else if (activeOrgRole) {
    displayRole = activeOrgRole;
  } else if (orgHelper) {
    const orgs = orgHelper.getOrgs();
    if (orgs && orgs.length > 0) {
      const firstOrg = orgs[0] as any;
      displayRole = firstOrg.userAssignedRole || firstOrg.userRole || firstOrg.orgRole || "Member";
      primaryOrgId = firstOrg.orgId;
    }
  }

  return {
    isLoggedIn: true,
    loading: false,
    role: displayRole,
    isAdmin,
    user,
    orgId: primaryOrgId,
    activeOrg,
    accessToken,
    isDemo: false,
  };
}
